(() => {
  if (customElements.get('social-videos-slider')) return;

  class SocialVideosSlider extends HTMLElement {
    constructor() {
      super();
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.swiper = null;
      this.isDragging = false;
    }

    connectedCallback() {
      this.sliderEl = this.querySelector('[data-slider]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');

      if (!this.sliderEl) return;

      this.classList.remove('is-initialized');

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }

      window.addEventListener('resize', this.onResize);

      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', this.onWindowLoad, { once: true });
        return;
      }

      this.initSwiper();
    }

    disconnectedCallback() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
      }

      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('resize', this.onResize);
      this.destroySwiper();
    }

    onWindowLoad() {
      this.initSwiper();
    }

    onSectionLoad(event) {
      if (!event.target.contains(this)) return;
      this.destroySwiper();
      this.initSwiper();
    }

    applySlidesOffsetAfter(swiper) {
      if (!swiper?.params) return;

      const width = window.innerWidth;

      if (width >= 1024) {
        swiper.params.slidesOffsetAfter = 0;
        return;
      }

      if (width >= 768) {
        swiper.params.slidesOffsetAfter = 20;
        return;
      }

      swiper.params.slidesOffsetAfter = 16;
    }

    onResize() {
      if (!this.swiper) return;
      this.applySlidesOffsetAfter(this.swiper);
      this.swiper.update();
    }

    preloadVisibleVideos() {
      if (this.isDragging || !this.swiper?.slides?.length) return;

      const slidesPerView = Number(this.swiper.params.slidesPerView) || 1;
      const preloadCount = Math.ceil(slidesPerView) + 1;
      const startIndex = this.swiper.activeIndex || 0;

      for (let index = startIndex; index < startIndex + preloadCount; index += 1) {
        const slide = this.swiper.slides[index];
        slide?.querySelector('social-videos-item')?.preloadVideo?.();
      }
    }

    onTouchStart() {
      this.isDragging = false;
    }

    onTouchMove() {
      if (this.isDragging) return;
      this.isDragging = true;
      this.classList.add('is-dragging');
    }

    onTouchEnd() {
      if (this.swiper?.animating) return;

      window.setTimeout(() => {
        this.isDragging = false;
        this.classList.remove('is-dragging');
        this.querySelectorAll('social-videos-item').forEach((item) => {
          item.updatePlayingState?.();
        });
      }, 50);
    }

    suspendActiveVideo() {
      this.querySelector('social-videos-item.is-playing')?.suspendPlayback?.();
    }

    initSwiper() {
      if (typeof Swiper === 'undefined' || this.swiper || !this.sliderEl) return;

      const slideCount = this.sliderEl.querySelectorAll('.swiper-slide').length;
      if (!slideCount) return;

      this.swiper = new Swiper(this.sliderEl, {
        slidesPerView: 1.227,
        slidesPerGroup: 1,
        spaceBetween: 16,
        breakpointsInverse: true,
        watchOverflow: true,
        allowTouchMove: slideCount > 1,
        simulateTouch: slideCount > 1,
        preventClicks: true,
        preventClicksPropagation: true,
        touchStartPreventDefault: false,
        threshold: 5,
        navigation:
          slideCount > 1
            ? {
                prevEl: this.prevButton,
                nextEl: this.nextButton,
              }
            : false,
        breakpoints: {
          600: {
            slidesPerView: 2.2,
            slidesPerGroup: 1,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2.5,
            slidesPerGroup: 1,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            slidesPerGroup: 1,
            spaceBetween: 23,
          },
          1300: {
            slidesPerView: 4,
            slidesPerGroup: 1,
            spaceBetween: 23,
          },
        },
        on: {
          breakpoint: () => {
            if (!this.swiper) return;
            this.applySlidesOffsetAfter(this.swiper);
            this.swiper.update();
          },
          init: () => {
            this.preloadVisibleVideos();
          },
          touchStart: () => {
            this.onTouchStart();
          },
          touchMove: () => {
            this.onTouchMove();
          },
          touchEnd: () => {
            this.onTouchEnd();
          },
          slideChangeTransitionStart: () => {
            this.isDragging = true;
            this.classList.add('is-dragging');
            this.suspendActiveVideo();
          },
          slideChangeTransitionEnd: () => {
            window.setTimeout(() => {
              this.isDragging = false;
              this.classList.remove('is-dragging');
              this.querySelectorAll('social-videos-item').forEach((item) => {
                item.updatePlayingState?.();
              });
              this.preloadVisibleVideos();
            }, 50);
          },
        },
      });

      this.applySlidesOffsetAfter(this.swiper);
      requestAnimationFrame(() => {
        this.swiper?.update();
        this.classList.add('is-initialized');
      });
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-initialized');
    }
  }

  customElements.define('social-videos-slider', SocialVideosSlider);
})();
